import FeedbackRegenerating from 'components/Grading/FeedbackRegenerating';
import FeedbackRenderer from 'components/Grading/FeedbackRenderer';
import FileSelector from 'components/Grading/FileSelector';
import FileViewer from 'components/Grading/FileViewer';
import GradingRegenerating from 'components/Grading/GradingRegenerating';
import LegacyFeedbackRenderer from 'components/Grading/LegacyFeedbackRenderer';
import RubricRenderer from 'components/Grading/RubricRenderer';
import Header from 'components/Preview/Header';
import { PageContext } from 'pages/Preview/PageProvider';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Box,
    Button,
    ButtonGroup,
    Dropdown,
    Icon,
    IconButton,
    PDFViewer,
    SheetViewer,
    Stack,
    styled,
    Text,
    VideoViewer,
} from 'shell/components';
import { getS3FileURL, getTooltipComponent } from 'utils/helper';

const Frame = ({ children }) => {
    const { t } = useTranslation('assignment');

    const {
        fineTuneDialog,
        isGradesGenerating,
        isFeedbackGenerating,
        isApplyingChanges,
        handleClickExit,
        handleClickFineTune,
        handleClickApplyChanges,
    } = useContext(PageContext);

    return (
        <>
            <FrameSides />
            <FrameTop alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Text color={'inherit'} variant={'p1'} weight={'semibold'}>
                    {t('fine-tuning.title', 'Preview and Fine-Tune')}
                </Text>
                <Button
                    className={'joyride-step-1'}
                    color={'neutral'}
                    disabled={isGradesGenerating || isFeedbackGenerating}
                    startIcon={<Icon kind={'wrench'} />}
                    variant={'secondary'}
                    onClick={handleClickFineTune}
                >
                    {t('fine-tuning.fine-tune-label', 'Fine-Tune')}
                </Button>
            </FrameTop>
            <FrameInner>{children}</FrameInner>
            <FrameBottom direction={'row'} justifyContent={'space-between'}>
                <Button color={'neutral'} disabled={isApplyingChanges} variant={'secondary'} onClick={handleClickExit}>
                    {t('common:controls.exit', 'Exit')}
                </Button>
                <Button
                    color={'neutral'}
                    disabled={!Boolean(fineTuneDialog.changes)}
                    loading={isApplyingChanges}
                    variant={'secondary'}
                    onClick={handleClickApplyChanges}
                >
                    {t('common:controls.apply-changes', 'Apply Changes')}
                </Button>
            </FrameBottom>
        </>
    );
};

const Mobile = ({
    currentAssignment,
    currentSubmission,
    selectedCriterionTitle,
    highlights,
    form,
    setForm,
    gradeOverrides,
    setGradeOverrides,
    meta,
    tab,
    setTab,
    fileURL,
    selectedCriterionId,
    setSelectedCriterionId,
    selectedFeedbackId,
    setSelectedFeedbackId,
    selectedInlineCommentId,
    setSelectedInlineCommentId,
    isGradesGenerating,
    isFeedbackGenerating,
    setShowFailedDialog,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
    handleClickRegenerateFeedback,
    handleSelectDropdown,
    handleCriterionNavigation,
}) => {
    const { t } = useTranslation('assignment');

    const url = useMemo(() => getS3FileURL(currentSubmission.files[0]), [currentSubmission]);

    const currentFile = currentSubmission.files[0];

    const renderableHighlights = highlights.map(({ id, value }) => {
        if (typeof value === 'string') return { id, value };
        return { id: id, value: value.highlight };
    });

    return (
        <Frame>
            <Header />
            <BodyStack isDocumentFullScreen={isDocumentFullScreen}>
                <TopStack isDocumentFullScreen={isDocumentFullScreen}>
                    {isDocumentFullScreen && (
                        <BannerBox>
                            <Text color={'inherit'}>
                                {tab === 'grades' && selectedCriterionId
                                    ? t(
                                          'grading-section.document-preview.criterion-annotations',
                                          'Currently viewing annotations for the criterion "{{criterionTitle}}."',
                                          {
                                              criterionTitle: selectedCriterionTitle,
                                          }
                                      )
                                    : tab === 'feedback' && selectedFeedbackId
                                      ? t(
                                            'grading-section.document-preview.feedback-annotations',
                                            'Currently viewing annotations for feedback.'
                                        )
                                      : t(
                                            'grading-section.document-preview.inline-comments-annotations',
                                            'Currently viewing annotations for inline comments.'
                                        )}{' '}
                            </Text>
                        </BannerBox>
                    )}
                    {currentFile.type === 'application/pdf' ? (
                        <PDFViewer
                            url={url}
                            highlights={renderableHighlights}
                            inlineComments={form.inline_comments}
                            setInlineComments={setForm}
                            selectedInlineCommentId={selectedInlineCommentId}
                            setSelectedInlineCommentId={setSelectedInlineCommentId}
                            isFullScreen={isDocumentFullScreen}
                            setIsFullScreen={setIsDocumentFullScreen}
                        />
                    ) : currentFile.type === 'text/csv' ? (
                        <SheetViewer highlights={renderableHighlights} url={url} />
                    ) : currentFile.type.startsWith('video/') || currentFile.type.startsWith('application/mp4') ? (
                        <VideoViewer
                            highlights={renderableHighlights}
                            mimeType={currentFile.type}
                            transcript={currentFile.transcript}
                            url={url}
                        />
                    ) : (
                        <FileViewer device={'desktop'} docUrl={url} fileType={currentFile.type} />
                    )}
                    <FileSelector submission={currentSubmission} selectedFileId={currentFile.id} onClick={() => {}} />
                </TopStack>
                <BottomStack id={'actions-container'} isDocumentFullScreen={isDocumentFullScreen}>
                    <ControlsStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <ButtonGroup
                            options={[
                                {
                                    key: 'grades',
                                    value: t('common:ui.grades', 'Grades'),
                                    disabled: Boolean(currentSubmission.is_failed),
                                },
                                {
                                    key: 'feedback',
                                    value: t('common:ui.feedback', 'Feedback'),
                                    badge: !meta.hasSeenFeedback,
                                    disabled: currentSubmission.is_failed ? false : !currentSubmission.ai.feedback,
                                },
                            ]}
                            selected={tab}
                            onChange={setTab}
                        />
                        <Stack direction={'row'} spacing={3}>
                            {tab === 'feedback' && currentSubmission.ai?.feedback?.feedback_type !== 'templated' && (
                                <Dropdown
                                    id={'preview-ai-options-dropdown'}
                                    options={[
                                        {
                                            key: 'regenerate-feedback',
                                            value: (
                                                <Stack spacing={1}>
                                                    <Stack direction={'row'} spacing={1}>
                                                        <Icon kind={'refresh'} />
                                                        <Text>
                                                            {t(
                                                                'grading-section.actions.regenerate-feedback',
                                                                'Regenerate Feedback'
                                                            )}
                                                        </Text>{' '}
                                                    </Stack>
                                                    <Text variant={'p3'}>
                                                        {t(
                                                            'grading-section.generate-new-feedback',
                                                            'Generate new feedback for this submission'
                                                        )}{' '}
                                                    </Text>
                                                </Stack>
                                            ),
                                        },
                                    ]}
                                    rootComponent={
                                        <IconButton
                                            aria-label={'see ai options'}
                                            disabled={isGradesGenerating || isFeedbackGenerating}
                                            kind={'ai'}
                                            size={'medium'}
                                            variant={'square'}
                                        />
                                    }
                                    onSelect={({ key }) => {
                                        if (key === 'regenerate-feedback') handleClickRegenerateFeedback();
                                    }}
                                />
                            )}
                            {currentSubmission.ai?.feedback?.feedback_type !== 'templated' && (
                                <Dropdown
                                    id={'preview-more-options-dropdown'}
                                    options={
                                        tab === 'grades'
                                            ? [
                                                  {
                                                      key: 'regenerate-grades',
                                                      value: t(
                                                          'grading-section.actions.regenerate-grades',
                                                          'Regenerate grades'
                                                      ),
                                                      disabled: isFeedbackGenerating,
                                                      icon: 'refresh',
                                                      tooltip: getTooltipComponent(
                                                          t(
                                                              'grading-section.actions.regenerate-grades',
                                                              'Regenerate Grades'
                                                          ),
                                                          t(
                                                              'grading-section.regenerate-grades-tooltip',
                                                              'In rare cases, AI grading may fail. You can regenerate grading suggestions, but feedback must be regenerated separately. Each regeneration is independent, so outcomes may vary.'
                                                          )
                                                      ),
                                                  },
                                              ]
                                            : [
                                                  {
                                                      key: 'copy-all',
                                                      value: t(
                                                          'grading-section.actions.copy-all-feedback',
                                                          'Copy all feedback'
                                                      ),
                                                      icon: 'copy',
                                                  },
                                                  {
                                                      key: 'delete',
                                                      value: t(
                                                          'grading-section.actions.delete-all-feedback',
                                                          'Delete all feedback'
                                                      ),
                                                      icon: 'delete',
                                                  },
                                              ]
                                    }
                                    rootComponent={
                                        <IconButton
                                            aria-label={'more options'}
                                            disabled={tab === 'grades' ? isGradesGenerating : isFeedbackGenerating}
                                            kind={'moreHorizontal'}
                                            size={'medium'}
                                            variant={'square'}
                                        />
                                    }
                                    onSelect={handleSelectDropdown}
                                />
                            )}
                        </Stack>
                    </ControlsStack>
                    {tab === 'grades' && (
                        <>
                            {isGradesGenerating ? (
                                <GradingRegenerating sx={{ top: '30%' }} />
                            ) : (
                                <RubricRenderer
                                    assignment={currentAssignment}
                                    currentSubmission={currentSubmission}
                                    selectedFileId={currentFile.id}
                                    setSelectedFileId={() => {}}
                                    locations={highlights}
                                    form={form}
                                    setForm={setForm}
                                    gradeOverrides={gradeOverrides}
                                    setGradeOverrides={setGradeOverrides}
                                    selectedCriterionId={selectedCriterionId}
                                    setSelectedCriterionId={setSelectedCriterionId}
                                    setShowFailedDialog={setShowFailedDialog}
                                    handleCriterionNavigation={handleCriterionNavigation}
                                />
                            )}
                        </>
                    )}
                    {tab === 'feedback' && (
                        <>
                            {isFeedbackGenerating ? (
                                <FeedbackRegenerating sx={{ top: '30%' }} />
                            ) : (
                                <Stack sx={{ flex: 1, overflow: 'auto' }}>
                                    {currentAssignment.ai_feedback_settings?.structure === 'templated' ||
                                    typeof currentSubmission.ai?.feedback === 'object' ? (
                                        <FeedbackRenderer
                                            isGradesGenerating={isGradesGenerating}
                                            assignment={currentAssignment}
                                            currentSubmission={currentSubmission}
                                            selectedFileId={currentFile.id}
                                            setSelectedFileId={() => {}}
                                            locations={highlights}
                                            form={form}
                                            setForm={setForm}
                                            selectedFeedbackId={selectedFeedbackId}
                                            setSelectedFeedbackId={setSelectedFeedbackId}
                                            handleClickRegenerateFeedback={handleClickRegenerateFeedback}
                                            handleCriterionNavigation={handleCriterionNavigation}
                                        />
                                    ) : (
                                        <LegacyFeedbackRenderer
                                            currentSubmission={currentSubmission}
                                            form={form}
                                            setForm={setForm}
                                            setShowFailedDialog={setShowFailedDialog}
                                        />
                                    )}
                                </Stack>
                            )}
                        </>
                    )}
                </BottomStack>
            </BodyStack>
        </Frame>
    );
};

const FrameTop = styled(Stack)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        width: '100%',
        height: '68px',
        padding: theme.spacing(2, 4),
        background: theme.palette.ensightful.primary[500],
        color: theme.palette.ensightful.shades[0],
    };
});

const FrameSides = styled(Stack)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        borderLeft: `5px solid ${theme.palette.ensightful.primary[500]}`,
        borderRight: `5px solid ${theme.palette.ensightful.primary[500]}`,
        width: '100vw',
        height: '100vh',
    };
});

const FrameBottom = styled(Stack)(({ theme }) => {
    return {
        position: 'absolute',
        bottom: 0,
        zIndex: theme.zIndex.drawer + 1,
        width: '100%',
        padding: theme.spacing(5),
        background: theme.palette.ensightful.primary[500],
    };
});

const FrameInner = styled(Stack)(({ theme }) => {
    return {
        position: 'absolute',
        top: '68px',
        left: '5px',
        zIndex: theme.zIndex.drawer + 1,
        width: 'calc(100% - 10px)',
        height: 'calc(100% - 68px - 76px)',
        background: theme.palette.ensightful.shades[0],
    };
});

const BodyStack = styled(Stack)(({ isDocumentFullScreen }) => {
    return {
        height: 'calc(100% - 109px)',
        ...(isDocumentFullScreen && {
            overflow: 'hidden',
        }),
    };
});

const TopStack = styled(Stack)(({ isDocumentFullScreen }) => {
    return {
        position: 'relative',
        height: isDocumentFullScreen ? '100%' : '45%',
        transition: 'height 150ms',
    };
});

const BannerBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(1, 5),
        background: theme.palette.ensightful.warning[200],
        color: theme.palette.ensightful.warning[900],
    };
});

const BottomStack = styled(Stack)(({ theme, isDocumentFullScreen }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        height: isDocumentFullScreen ? 0 : '55%',
        background: theme.palette.ensightful.shades[0],
        transition: 'height 150ms',
    };
});

const ControlsStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(3, 5),
    };
});

export default Mobile;
